import { apiClient } from '../../../lib/apiClient';

/* =========================================================
 * 定数 / ユーティリティ
 * ========================================================= */

// apiClient.baseURL でドメインは補完されるため、パスのみを使用する
export const WEEKLY_INPUT_ITEMS_PATH = '/weekly-input-items';
export const TEAM_DEPT_BY_DEPT_PATH = '/by-dept';
export const USER_DEPARTMENTS_PATH = '/user-info';
export const APPROVE3_MEMBERS_PATH = '/get_approve3_members';
export const APPROVEAM_MEMBERS_PATH = '/get_approveam_members';

export const normUpper = (s?: string | null) => (s ?? '').trim().toUpperCase();
export const normalizeEmail = (s?: string | null) => (s ?? '').trim().toLowerCase();
export const uniq: <T>(arr: T[]) => T[] = (arr) => Array.from(new Set(arr));

export const LOG_PREFIX = '[SearchArea]';

// 文字列 → トークン分割
export const splitTokens = (raw: any): string[] =>
  String(raw ?? '')
    .split(/[,\u3001\u3002\uFF0C\uFF0E\s]+/)
    .map((s) => s.normalize('NFKC').trim())
    .filter(Boolean);

// 値 → 文字列化
export const pickValueLike = (v: any): string => {
  if (v == null) return '';
  if (Array.isArray(v)) {
    return v
      .map((x) =>
        typeof x === 'object' ? String((x as any).label ?? (x as any).value ?? (x as any).name ?? '') : String(x)
      )
      .map((s) => s.normalize('NFKC').trim())
      .filter(Boolean)
      .join(',');
  }
  if (typeof v === 'object') {
    return String((v as any).label ?? (v as any).value ?? (v as any).name ?? JSON.stringify(v));
  }
  return String(v);
};

export const toNum = (v: any): number | undefined => {
  const n = Number(v);
  return Number.isFinite(n) ? n : undefined;
};

export type ManagedAttrLike = {
  attr_id?: unknown;
  attrId?: unknown;
  attr_name?: unknown;
  attrName?: unknown;
  label?: unknown;
  department_id?: unknown;
  departmentId?: unknown;
  dept_id?: unknown;
  team_id?: unknown;
  teamId?: unknown;
  attr_required?: unknown;
  attrRequired?: unknown;
  order_number?: unknown;
  orderNumber?: unknown;
  status?: unknown;
  visible?: unknown;
  is_visible?: unknown;
  isVisible?: unknown;
};

const toScopeNum = (v: unknown): number => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const toAttrRequiredFlag = (raw: unknown): 0 | 1 | null => {
  if (raw == null) return null;
  if (typeof raw === 'boolean') return raw ? 1 : 0;
  const text = String(raw).normalize('NFKC').trim().toLowerCase();
  if (!text) return null;

  const n = Number(text);
  if (Number.isFinite(n)) return n === 0 ? 0 : n === 1 ? 1 : null;

  if (text === 'false') return 0;
  if (text === 'true') return 1;

  return null;
};

export const isFreeInputManagedAttr = (attr?: ManagedAttrLike | null): boolean => {
  return toAttrRequiredFlag(attr?.attr_required ?? attr?.attrRequired) === 0;
};

export const isGlobalManagedAttr = (attr?: ManagedAttrLike | null): boolean => {
  if (!attr) return false;
  const deptId = toScopeNum(attr.department_id ?? attr.departmentId ?? attr.dept_id);
  const teamId = toScopeNum(attr.team_id ?? attr.teamId);
  return deptId === 0 && teamId === 0;
};

export const isCommonDisplayManagedAttr = (attr?: ManagedAttrLike | null): boolean => {
  return toAttrRequiredFlag(attr?.attr_required ?? attr?.attrRequired) === 1;
};

const toVisibleFlag = (raw: unknown): boolean | null => {
  if (raw == null || raw === '') return null;
  if (typeof raw === 'boolean') return raw;

  const text = String(raw).normalize('NFKC').trim().toLowerCase();
  if (!text) return null;

  if (['false', '0', 'n', 'no', 'off'].includes(text)) return false;
  if (['true', '1', 'y', 'yes', 'on'].includes(text)) return true;

  return null;
};

const toStatusVisible = (raw: unknown): boolean => {
  const flag = toVisibleFlag(raw);
  if (flag !== null) return flag;

  const text = String(raw ?? '').normalize('NFKC').trim().toUpperCase();
  if (!text) return true;

  return text !== 'N';
};

export const isManagedAttrVisible = (attr?: ManagedAttrLike | null): boolean => {
  if (!attr) return false;

  const visibleFlag = toVisibleFlag(attr.visible ?? attr.is_visible ?? attr.isVisible);
  if (visibleFlag === false) return false;

  return toStatusVisible(attr.status);
};

export const normalizeManagedAttrNameKey = (value: unknown): string =>
  String(value ?? '')
    .normalize('NFKC')
    .trim()
    .replace(/\s+/g, '')
    .toLowerCase();

const getManagedAttrName = (attr: ManagedAttrLike): string =>
  String(attr.attr_name ?? attr.attrName ?? attr.label ?? '').trim();

const getManagedAttrId = (attr: ManagedAttrLike): number => {
  const n = Number(attr.attr_id ?? attr.attrId);
  return Number.isFinite(n) ? n : Number.MAX_SAFE_INTEGER;
};

const getManagedAttrOrder = (attr: ManagedAttrLike): number => {
  const n = Number(attr.order_number ?? attr.orderNumber);
  return Number.isFinite(n) ? n : Number.MAX_SAFE_INTEGER;
};

export const getManagedAttrScopeRank = (
  attr: ManagedAttrLike,
  departmentId?: number | null,
  teamId?: number | null
): number => {
  const depId = toScopeNum(departmentId);
  const tmId = toScopeNum(teamId);
  const attrDepId = toScopeNum(attr.department_id ?? attr.departmentId ?? attr.dept_id);
  const attrTeamId = toScopeNum(attr.team_id ?? attr.teamId);

  if (depId > 0 && tmId > 0 && attrDepId === depId && attrTeamId === tmId) {
    return 300;
  }

  if (depId > 0 && attrDepId === depId && attrTeamId === 0) {
    return 200;
  }

  if (attrDepId === 0 && attrTeamId === 0) {
    return 100;
  }

  return -1;
};

export const selectManagedAttrsForScope = <T extends ManagedAttrLike>(
  attrs: T[] | null | undefined,
  opts: {
    departmentId?: number | null;
    teamId?: number | null;
    includeUnscopedFallback?: boolean;
  } = {}
): T[] => {
  const list = Array.isArray(attrs)
    ? attrs.filter((attr) => normalizeManagedAttrNameKey(getManagedAttrName(attr)))
    : [];
  if (list.length === 0) return [];

  const scoped = list.filter(
    (attr) => getManagedAttrScopeRank(attr, opts.departmentId, opts.teamId) > 0
  );
  const source =
    scoped.length > 0 || !opts.includeUnscopedFallback
      ? scoped
      : list;

  const bestByName = new Map<string, T>();
  source.forEach((attr) => {
    const key = normalizeManagedAttrNameKey(getManagedAttrName(attr));
    if (!key) return;

    const current = bestByName.get(key);
    if (!current) {
      bestByName.set(key, attr);
      return;
    }

    const rankDiff =
      getManagedAttrScopeRank(attr, opts.departmentId, opts.teamId) -
      getManagedAttrScopeRank(current, opts.departmentId, opts.teamId);
    if (rankDiff > 0) {
      bestByName.set(key, attr);
      return;
    }

    if (rankDiff === 0) {
      const orderDiff = getManagedAttrOrder(attr) - getManagedAttrOrder(current);
      if (orderDiff < 0 || (orderDiff === 0 && getManagedAttrId(attr) < getManagedAttrId(current))) {
        bestByName.set(key, attr);
      }
    }
  });

  return Array.from(bestByName.values()).sort(
    (a, b) =>
      getManagedAttrOrder(a) - getManagedAttrOrder(b) ||
      getManagedAttrName(a).localeCompare(getManagedAttrName(b), 'ja') ||
      getManagedAttrId(a) - getManagedAttrId(b)
  );
};

export type CognitoAttribute = {
  Name: string;
  Value: string;
};

export const extractEmailFromCognito = (attrs?: CognitoAttribute[] | null): string | null => {
  if (!Array.isArray(attrs)) return null;
  const rec = attrs.find((a) => String(a?.Name ?? '').toLowerCase() === 'email' && a?.Value);
  return rec ? String(rec.Value).trim() : null;
};

// snake_case 変換（日本語/全角→NFKC正規化後、非英数字を _ に）
export const toSnake = (s: string): string =>
  String(s ?? '')
    .normalize('NFKC')
    .trim()
    .replace(/[A-Z]+(?=[A-Z][a-z])/g, (m) => m.toLowerCase() + '_')
    .replace(/[\s\u3000\-–—―・/／\\]+/g, '_')
    .replace(/[^0-9A-Za-z_]+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_|_$/g, '')
    .toLowerCase();

// 表示/オブジェクト混在値 → 文字列（未選択は ''）
export const toText = (v: any): string => {
  if (v == null) return '';
  if (typeof v === 'object') {
    return String((v as any).label ?? (v as any).value ?? (v as any).name ?? '').trim();
  }
  return String(v).trim();
};

// 文字列化して空なら null を返す
export const toNullableText = (v: any): string | null => {
  const s = toText(v);
  return s === '' ? null : s;
};

// ★ 追加：日付フォーマットの相互変換
//   - state(startDate/endDate): YYYY-MM-DD を基本
//   - dynamic field(開始終了日付): YYYY/MM/DD を基本
export const normalizeDateForState = (raw: string): string => {
  const s = String(raw ?? '').trim();
  const m = s.match(/^(\d{4})[-/](\d{1,2})[-/](\d{1,2})$/);
  if (!m) return s;

  const yyyy = m[1];
  const mm = m[2].padStart(2, '0');
  const dd = m[3].padStart(2, '0');

  return `${yyyy}-${mm}-${dd}`;
};

export const normalizeDateForField = (raw: string): string => {
  const s = String(raw ?? '').trim();
  const m = s.match(/^(\d{4})[-/](\d{1,2})[-/](\d{1,2})$/);
  if (!m) return s;

  const yyyy = m[1];
  const mm = m[2].padStart(2, '0');
  const dd = m[3].padStart(2, '0');

  return `${yyyy}/${mm}/${dd}`;
};

/* =========================================================
 * NEW作成時「前回選択したチーム」を localStorage に保存/復元
 * ========================================================= */

export type LastNewTeamCache = {
  team?: string | null;
  team_id?: number | null;
  savedAt: number;
};

const LAST_NEW_TEAM_KEY_PREFIX = 'weekly_last_new_team_v1';

const makeLastNewTeamKey = (userKey: string, deptId: number) =>
  `${LAST_NEW_TEAM_KEY_PREFIX}:${userKey}:${deptId}`;

export const readLastNewTeamCache = (userKey: string, deptId: number): LastNewTeamCache | null => {
  try {
    const k = makeLastNewTeamKey(userKey, deptId);
    const raw = localStorage.getItem(k);
    if (!raw) return null;
    const v = JSON.parse(raw);
    if (!v || typeof v !== 'object') return null;
    if (typeof (v as any).savedAt !== 'number') return null;
    return v as LastNewTeamCache;
  } catch {
    return null;
  }
};

export const writeLastNewTeamCache = (userKey: string, deptId: number, val: LastNewTeamCache) => {
  try {
    const k = makeLastNewTeamKey(userKey, deptId);
    localStorage.setItem(k, JSON.stringify(val));
  } catch {
    /* noop */
  }
};

/* =========================================================
 * NEW作成時「前回入力値」を localStorage に保存/復元
 * ========================================================= */

export type LastNewDraftField = {
  id: string;
  type: string;
  label?: string;
  attr_id?: number;
  value: any;
  isRemovable?: boolean;
};

export type LastNewDraftTaskMember = {
  // 一意キー（メール等）。これが無いと checkbox の復元で ✅ が外れやすい
  value?: string;

  label: string;
  lastName?: string;
  firstName?: string;
  percent?: number;
};

export type LastNewDraftCache = {
  team?: string | null;
  team_id?: number | null;
  reporter?: string | null;
  approver3?: string | null;
  approverAM?: string | null;
  reportType?: string | null;
  shikimuke?: string | null;
  lot?: string | null;

  startDate?: string | null;
  endDate?: string | null;

  taskMembers?: LastNewDraftTaskMember[];
  freeWords?: Record<string, string>;

  fields?: LastNewDraftField[];
  savedAt: number;
};

const LAST_NEW_DRAFT_KEY_PREFIX = 'weekly_last_new_draft_v2';
const LEGACY_LAST_NEW_DRAFT_KEY_PREFIX = 'weekly_last_new_draft_v1';
const LAST_NEW_DRAFT_TTL_MS = 14 * 24 * 60 * 60 * 1000;
const LAST_NEW_DRAFT_MAX_FUTURE_SKEW_MS = 5 * 60 * 1000;
const TASK_MEMBERS_MAX_COUNT = 10;

const makeLastNewDraftKey = (userKey: string, deptId: number, prefix = LAST_NEW_DRAFT_KEY_PREFIX) =>
  `${prefix}:${userKey}:${deptId}`;

const isLastNewDraftKey = (key: string): boolean =>
  key.startsWith(`${LAST_NEW_DRAFT_KEY_PREFIX}:`) ||
  key.startsWith(`${LEGACY_LAST_NEW_DRAFT_KEY_PREFIX}:`);

const safeLocalStorageGet = (key: string): string | null => {
  try {
    return localStorage.getItem(key);
  } catch (e) {
    console.warn(`${LOG_PREFIX} localStorage get failed: ${key}`, e);
    return null;
  }
};

const safeLocalStorageRemove = (key: string): void => {
  try {
    localStorage.removeItem(key);
  } catch (e) {
    console.warn(`${LOG_PREFIX} localStorage remove failed: ${key}`, e);
  }
};

const isFreshLastNewDraftSavedAt = (savedAt: unknown, now = Date.now()): boolean => {
  const n = Number(savedAt);
  if (!Number.isFinite(n)) return false;
  if (n > now + LAST_NEW_DRAFT_MAX_FUTURE_SKEW_MS) return false;
  return now - n <= LAST_NEW_DRAFT_TTL_MS;
};

const pruneInvalidLastNewDraftCaches = (): void => {
  try {
    const keys: string[] = [];
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);
      if (key && isLastNewDraftKey(key)) keys.push(key);
    }

    keys.forEach((key) => {
      const raw = localStorage.getItem(key);
      if (!raw) return;

      const parsed = safeJsonParse<any>(raw);
      if (!parsed || typeof parsed !== 'object' || !isFreshLastNewDraftSavedAt(parsed.savedAt)) {
        safeLocalStorageRemove(key);
      }
    });
  } catch (e) {
    console.warn(`${LOG_PREFIX} localStorage prune failed`, e);
  }
};

const safeLocalStorageSet = (key: string, value: string): boolean => {
  try {
    localStorage.setItem(key, value);
    return true;
  } catch (e) {
    console.warn(`${LOG_PREFIX} localStorage set failed: ${key}`, e);
  }

  if (isLastNewDraftKey(key)) {
    pruneInvalidLastNewDraftCaches();
    try {
      localStorage.setItem(key, value);
      return true;
    } catch (retryError) {
      console.warn(`${LOG_PREFIX} localStorage set retry failed: ${key}`, retryError);
    }
  }

  return false;
};

const safeJsonParse = <T,>(raw: string): T | null => {
  try {
    return JSON.parse(raw) as T;
  } catch (e) {
    console.warn(`${LOG_PREFIX} localStorage JSON parse failed`, e);
    return null;
  }
};

export const sanitizeDraftFields = (fields: any[]): LastNewDraftField[] => {
  const list = Array.isArray(fields) ? fields : [];

  return list
    .map((f: any) => {
      const type = String(f?.type ?? '').trim();
      const id = String(f?.id ?? '').trim();
      if (!type || !id) return null;

      const value = (f as any).value;

      const attrId = Number((f as any)?.attr_id);

      return {
        id,
        type,
        label: f?.label != null ? String(f.label) : undefined,
        attr_id: Number.isFinite(attrId) ? attrId : undefined,
        value,
        isRemovable: !!f?.isRemovable,
      } as LastNewDraftField;
    })
    .filter(Boolean) as LastNewDraftField[];
};

// ★ 追加：fields の「開始終了日付」を top-level(start/end) と同期して返す
export const patchDateRangeFieldInDraftFields = (
  fields: LastNewDraftField[] | undefined,
  startDate?: string,
  endDate?: string
): LastNewDraftField[] | undefined => {
  if (!Array.isArray(fields) || fields.length === 0) return fields;

  let changed = false;

  const patched = fields.map((f) => {
    if (String((f as any)?.type ?? '').trim() !== '開始終了日付') return f;

    const prevFrom = String((f as any)?.value?.from ?? '');
    const prevTo = String((f as any)?.value?.to ?? '');

    const nextFrom =
      typeof startDate === 'undefined'
        ? prevFrom
        : startDate
        ? normalizeDateForField(startDate)
        : '';
    const nextTo =
      typeof endDate === 'undefined'
        ? prevTo
        : endDate
        ? normalizeDateForField(endDate)
        : '';

    if (prevFrom !== nextFrom || prevTo !== nextTo) changed = true;

    return {
      ...f,
      value: {
        ...(f as any).value,
        from: nextFrom,
        to: nextTo,
      },
    } as LastNewDraftField;
  });

  return changed ? patched : fields;
};

export const sanitizeTaskMembers = (members: any): LastNewDraftTaskMember[] => {
  const list = Array.isArray(members) ? members : [];

  const looksLikeEmail = (s: string): boolean => {
    const v = String(s ?? '').trim().toLowerCase();
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
  };

  const pickValue = (m: any, fallbackLabel: string): string | undefined => {
    // 1) { value } があるなら最優先
    const direct = typeof m?.value === 'string' ? m.value.trim() : '';
    if (direct) return direct;

    // 2) よくある email 系キーを吸収
    const candidates = [
      m?.mail_address,
      m?.email,
      m?.mail,
      m?.email_address,
      m?.loginId,
      m?.login_id,
      m?.user_email,
      m?.user_mail,
    ]
      .filter((x: any) => typeof x === 'string')
      .map((x: any) => String(x).trim());

    const hit = candidates.find((x) => x.length > 0);
    if (hit) return hit;

    // 3) 文字列だけ来た場合：メールっぽければそれを value にする
    if (typeof m === 'string') {
      const s = m.trim();
      if (looksLikeEmail(s)) return s;
    }

    // 4) それでも無いなら label を value にする（同名重複はこの場合潰れ得る）
    return fallbackLabel ? fallbackLabel : undefined;
  };

  const toOne = (m: any): LastNewDraftTaskMember | null => {
    if (m == null) return null;

    // 文字列だけ来た場合も吸収
    if (typeof m === 'string') {
      const s = String(m ?? '').trim();
      if (!s) return null;
      const parts = s.split(/\s+/).filter(Boolean);
      const lastName = parts[0] ?? '';
      const firstName = parts.slice(1).join(' ') ?? '';

      const value = pickValue(m, s);

      return {
        value,
        label: s,
        lastName: lastName || undefined,
        firstName: firstName || undefined,
      } as LastNewDraftTaskMember;
    }

    const labelRaw = String(m?.label ?? '').trim();
    const lastNameRaw = String(m?.lastName ?? m?.last_name ?? '').trim();
    const firstNameRaw = String(m?.firstName ?? m?.first_name ?? '').trim();
    const percent = toNum(m?.percent ?? m?.rate);

    const label = labelRaw || [lastNameRaw, firstNameRaw].filter(Boolean).join(' ').trim();

    if (!label && !lastNameRaw && !firstNameRaw) return null;

    const value = pickValue(m, label);

    const out: LastNewDraftTaskMember = {
      value,
      label,
      lastName: lastNameRaw || undefined,
      firstName: firstNameRaw || undefined,
    };
    if (typeof percent === 'number') out.percent = percent;

    return out;
  };

  // 重要：重複除去は label ではなく value（メール等）で行う
  const seen = new Set<string>();
  const out: LastNewDraftTaskMember[] = [];

  list.forEach((m: any) => {
    const v = toOne(m);
    if (!v) return;

    const key = String(v.value ?? v.label ?? '').trim();
    if (!key) return;

    if (seen.has(key)) return;
    seen.add(key);
    out.push(v);
  });

  return out.slice(0, TASK_MEMBERS_MAX_COUNT);
};

/**
 * 追加：draft fields の「連名者」を正として taskMembers を導出する
 *  - ログの通り fields は空なのに taskMembers が残ると復元で ✅ が戻るため、復元時に同期する
 */
export const deriveTaskMembersFromDraftFields = (
  fields: LastNewDraftField[] | undefined
): LastNewDraftTaskMember[] | null => {
  if (!Array.isArray(fields) || fields.length === 0) return null;

  const hit = fields.find((f) => String((f as any)?.type ?? '').trim() === '連名者');
  if (!hit) return null;

  const raw = (hit as any).value;

  // value は配列が基本だが、単体オブジェクト/文字列でも来る可能性があるので配列化して吸収
  const asArray = Array.isArray(raw) ? raw : raw != null ? [raw] : [];

  // sanitizeTaskMembers は配列以外を捨てるので、ここで配列化して渡す
  return sanitizeTaskMembers(asArray);
};

export const areTaskMembersEqual = (a: any, b: any): boolean => {
  const aa = sanitizeTaskMembers(a);
  const bb = sanitizeTaskMembers(b);

  const keyOf = (x: LastNewDraftTaskMember) => {
    const v = String(x?.value ?? '').trim();
    const l = String(x?.label ?? '').trim();
    return v || l;
  };

  const normOne = (x: LastNewDraftTaskMember) => {
    return {
      k: keyOf(x),
      label: String(x?.label ?? ''),
      lastName: String(x?.lastName ?? ''),
      firstName: String(x?.firstName ?? ''),
      percent: typeof x?.percent === 'number' ? x.percent : undefined,
    };
  };

  const na = aa.map(normOne).filter((x) => x.k).sort((x, y) => x.k.localeCompare(y.k, 'ja'));
  const nb = bb.map(normOne).filter((x) => x.k).sort((x, y) => x.k.localeCompare(y.k, 'ja'));

  if (na.length !== nb.length) return false;

  for (let i = 0; i < na.length; i++) {
    const x = na[i];
    const y = nb[i];

    if (x.k !== y.k) return false;
    if (x.label !== y.label) return false;
    if (x.lastName !== y.lastName) return false;
    if (x.firstName !== y.firstName) return false;

    const px = typeof x?.percent === 'number' ? x.percent : undefined;
    const py = typeof y?.percent === 'number' ? y.percent : undefined;
    if (px !== py) return false;
  }

  return true;
};

export const sanitizeFreeWords = (fw: any): Record<string, string> => {
  if (!fw || typeof fw !== 'object' || Array.isArray(fw)) return {};
  const out: Record<string, string> = {};

  Object.entries(fw).forEach(([k, v]) => {
    const key = String(k ?? '').trim();
    if (!key) return;
    out[key] = String(v ?? '').normalize('NFKC');
  });

  return out;
};

export const areFreeWordsEqual = (a: any, b: any): boolean => {
  const aa = sanitizeFreeWords(a);
  const bb = sanitizeFreeWords(b);

  const keys = uniq([...Object.keys(aa), ...Object.keys(bb)]).sort();
  for (const k of keys) {
    if (String(aa[k] ?? '') !== String(bb[k] ?? '')) return false;
  }
  return true;
};

export const hasAnyFreeWordsValue = (fw: any): boolean => {
  const s = sanitizeFreeWords(fw);
  return Object.values(s).some((v) => String(v ?? '').trim() !== '');
};

const normalizeLastNewDraftCache = (
  raw: any,
  opts: { includeFields: boolean; deriveTaskMembersFromFields: boolean; enforceTtl?: boolean }
): LastNewDraftCache | null => {
  if (!raw || typeof raw !== 'object') return null;
  if (typeof raw.savedAt !== 'number') return null;
  if (opts.enforceTtl !== false && !isFreshLastNewDraftSavedAt(raw.savedAt)) return null;

  const fields = opts.includeFields && Array.isArray(raw.fields)
    ? sanitizeDraftFields(raw.fields)
    : undefined;
  const fieldsForTaskMembers = opts.deriveTaskMembersFromFields && Array.isArray(raw.fields)
    ? sanitizeDraftFields(raw.fields)
    : undefined;
  const taskMembers = Array.isArray(raw.taskMembers)
    ? sanitizeTaskMembers(raw.taskMembers)
    : undefined;

  const freeWords =
    raw.freeWords && typeof raw.freeWords === 'object' && !Array.isArray(raw.freeWords)
      ? sanitizeFreeWords(raw.freeWords)
      : undefined;

  const derived = deriveTaskMembersFromDraftFields(fieldsForTaskMembers);
  const taskMembersSynced = derived !== null ? derived : taskMembers;

  return {
    team: raw.team != null ? String(raw.team) : raw.team,
    team_id: typeof toNum(raw.team_id) === 'number' ? toNum(raw.team_id) : null,
    reporter: raw.reporter != null ? String(raw.reporter) : raw.reporter,
    approver3: raw.approver3 != null ? String(raw.approver3) : raw.approver3,
    approverAM: raw.approverAM != null ? String(raw.approverAM) : raw.approverAM,
    reportType: raw.reportType != null ? String(raw.reportType) : raw.reportType,
    shikimuke: raw.shikimuke != null ? String(raw.shikimuke) : raw.shikimuke,
    lot: raw.lot != null ? String(raw.lot) : raw.lot,
    startDate: raw.startDate != null ? String(raw.startDate) : raw.startDate,
    endDate: raw.endDate != null ? String(raw.endDate) : raw.endDate,
    taskMembers: taskMembersSynced,
    freeWords,
    fields,
    savedAt: raw.savedAt,
  } as LastNewDraftCache;
};

export const readLastNewDraftCache = (userKey: string, deptId: number): LastNewDraftCache | null => {
  const key = makeLastNewDraftKey(userKey, deptId);
  const raw = safeLocalStorageGet(key);
  if (raw) {
    const parsed = safeJsonParse<any>(raw);
    const normalized = normalizeLastNewDraftCache(parsed, {
      includeFields: true,
      deriveTaskMembersFromFields: true,
    });
    if (normalized) return normalized;
    safeLocalStorageRemove(key);
  }

  const legacyKey = makeLastNewDraftKey(userKey, deptId, LEGACY_LAST_NEW_DRAFT_KEY_PREFIX);
  const legacyRaw = safeLocalStorageGet(legacyKey);
  if (!legacyRaw) return null;

  const legacyParsed = safeJsonParse<any>(legacyRaw);
  const migrated = normalizeLastNewDraftCache(legacyParsed, {
    includeFields: false,
    deriveTaskMembersFromFields: true,
  });
  if (!migrated) {
    safeLocalStorageRemove(legacyKey);
    return null;
  }

  // v1 の動的 fields は stale の可能性があるため移行しない。
  safeLocalStorageSet(key, JSON.stringify(migrated));
  return migrated;
};

export const writeLastNewDraftCache = (userKey: string, deptId: number, val: LastNewDraftCache): boolean => {
  const k = makeLastNewDraftKey(userKey, deptId);
  try {
    const now = Date.now();
    const savedAt = isFreshLastNewDraftSavedAt(val?.savedAt, now)
      ? Number(val.savedAt)
      : now;
    const normalized = normalizeLastNewDraftCache(
      {
        ...val,
        savedAt,
      },
      {
        includeFields: true,
        deriveTaskMembersFromFields: false,
        enforceTtl: false,
      }
    );
    if (!normalized) return false;
    return safeLocalStorageSet(k, JSON.stringify(normalized));
  } catch (e) {
    console.warn(`${LOG_PREFIX} localStorage stringify failed: ${k}`, e);
    return false;
  }
};

/* =========================================================
 * /user-info：1回だけ取得（セッションキャッシュを使わない）
 * ========================================================= */

export type UserInfoRow = {
  department: string | null;
  department_id?: number | null;
  first_name?: string | null;
  last_name?: string | null;
  mail_address: string;
  team?: string | null;
};

export type UserInfoApiResponse = {
  total: number;
  users: UserInfoRow[];
};

const USER_INFO_SESSION_KEY = 'weekly_user_info_cache_v1';

let USER_INFO_CACHE: UserInfoApiResponse | null = null;
let USER_INFO_PROMISE: Promise<UserInfoApiResponse> | null = null;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const readUserInfoFromSession = (): UserInfoApiResponse | null => {
  try {
    const raw = sessionStorage.getItem(USER_INFO_SESSION_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw);
    if (data && typeof data === 'object' && Array.isArray((data as any).users)) {
      return data as UserInfoApiResponse;
    }
  } catch {
    /* noop */
  }
  return null;
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const writeUserInfoToSession = (data: UserInfoApiResponse) => {
  try {
    sessionStorage.setItem(USER_INFO_SESSION_KEY, JSON.stringify(data));
  } catch {
    /* noop */
  }
};

export const fetchUserInfoOnce = async (): Promise<UserInfoApiResponse> => {
  // 毎回最新取得（セッションキャッシュは使わない）
  const url = `${USER_DEPARTMENTS_PATH}`;
  try {
    sessionStorage.removeItem(USER_INFO_SESSION_KEY);
  } catch {
    /* noop */
  }
  USER_INFO_CACHE = null;
  USER_INFO_PROMISE = null;

  const res = await apiClient.get<UserInfoApiResponse>(url, {
    headers: { Accept: 'application/json' },
  });
  const data = Array.isArray(res.data?.users)
    ? res.data
    : ({ total: 0, users: [] } as UserInfoApiResponse);

  return data;
};

/* =========================================================
 * /by-dept：キャッシュあり
 * ========================================================= */

export type TeamDepartmentLink = {
  team_id: number;
  team_label?: string;
  team_value?: string | null;
  department_id: number;
  order_number?: number | null;
};

type ByDeptCacheEntry = {
  data: TeamDepartmentLink[];
  etag?: string | null;
  lastModified?: string | null;
  savedAt: number;
};

const BY_DEPT_SESSION_KEY = 'weekly_by_dept_cache_v1';

let BY_DEPT_CACHE: Record<string, ByDeptCacheEntry> = (() => {
  try {
    const raw = sessionStorage.getItem(BY_DEPT_SESSION_KEY);
    return raw ? (JSON.parse(raw) as Record<string, ByDeptCacheEntry>) : {};
  } catch {
    return {};
  }
})();

let BY_DEPT_INFLIGHT: Record<string, Promise<TeamDepartmentLink[]> | undefined> = {};

const saveByDeptCacheToSession = () => {
  try {
    sessionStorage.setItem(BY_DEPT_SESSION_KEY, JSON.stringify(BY_DEPT_CACHE));
  } catch {
    /* noop */
  }
};

export const fetchByDeptWithCache = async (
  deptId: number,
  includeInactive: boolean
): Promise<TeamDepartmentLink[]> => {
  const key = `${deptId}:${includeInactive ? 1 : 0}`;

  if (BY_DEPT_INFLIGHT[key]) return BY_DEPT_INFLIGHT[key]!;

  const cached = BY_DEPT_CACHE[key];
  const url = `${TEAM_DEPT_BY_DEPT_PATH}`;
  const headers: Record<string, string> = { Accept: 'application/json' };
  if (cached?.etag) headers['If-None-Match'] = String(cached.etag);
  if (!cached?.etag && cached?.lastModified) headers['If-Modified-Since'] = String(cached.lastModified);

  const params = { department_id: deptId, include_inactive: includeInactive } as const;

  BY_DEPT_INFLIGHT[key] = (async () => {
    try {
      const res = await apiClient.get<TeamDepartmentLink[]>(url, {
        headers,
        params,
        validateStatus: (s: number) => (s >= 200 && s < 300) || s === 304,
      });

      if (res.status === 304 && cached) return cached.data;

      const data = Array.isArray(res.data) ? res.data : [];
      const etag = (res.headers?.etag as string) ?? (res.headers?.ETag as string) ?? null;
      const lastModified =
        (res.headers?.['last-modified'] as string) ??
        (res.headers?.['Last-Modified'] as string) ??
        null;

      BY_DEPT_CACHE[key] = { data, etag, lastModified, savedAt: Date.now() };
      saveByDeptCacheToSession();

      return data;
    } finally {
      delete BY_DEPT_INFLIGHT[key];
    }
  })();

  return BY_DEPT_INFLIGHT[key]!;
};

/* =========================================================
 * "週報ベース属性" 正規化（ゆらぎ吸収）
 * ========================================================= */

const ATTR_CONTAINER_KEYS = [
  'weekly_base_attributes',
  'weeklyBaseAttributes',
  'base_attributes',
  'baseAttributes',
  'attributes',
  'attrs',
  'attribute_values',
  'attributeValues',
] as const;

type NormalizedAttr = { attr_id?: number; attr_name: string; attr_value: string };

const normalizeAttrContainer = (src: any): NormalizedAttr[] => {
  const out: NormalizedAttr[] = [];

  const pushSafe = (e: any) => {
    const id = toNum(e?.attr_id ?? e?.id ?? e?.attribute_id ?? e?.option_id);
    const nameRaw =
      e?.attr_name ??
      e?.name ??
      e?.label ??
      e?.key ??
      (typeof e?.field === 'string' ? e.field : undefined);
    const valueRaw = e?.attr_value ?? e?.value ?? e?.text ?? e?.content ?? e?.val ?? e?.display;

    const attr_name = nameRaw != null ? String(nameRaw).trim() : '';
    const attr_value = pickValueLike(valueRaw);

    if (!attr_name && id == null) return;
    if (!attr_value) return;

    out.push({ attr_id: id, attr_name, attr_value });
  };

  if (Array.isArray(src)) {
    src.forEach((e) => pushSafe(e));
    return out;
  }

  if (src && typeof src === 'object') {
    if ('attr_name' in src || 'attr_id' in src) {
      if ('attr_value' in src || 'value' in src || 'text' in src) {
        pushSafe(src);
        return out;
      }
    }

    Object.entries(src).forEach(([k, v]) => {
      const id = toNum(k);
      const attr_name = id != null ? '' : String(k).trim();
      const attr_value = pickValueLike(v);
      if (!attr_name && id == null) return;
      if (!attr_value) return;
      out.push({ attr_id: id ?? undefined, attr_name, attr_value });
    });

    return out;
  }

  return out;
};

const findRawAttrContainer = (item: any): any => {
  if (!item || typeof item !== 'object') return null;
  for (const key of ATTR_CONTAINER_KEYS) {
    if (key in item) return (item as any)[key];
  }
  for (const v of Object.values(item)) {
    if (!v || typeof v !== 'object') continue;
    for (const key of ATTR_CONTAINER_KEYS) {
      if (key in (v as any)) return (v as any)[key];
    }
  }
  return null;
};

export const extractWeeklyBaseAttributes = (item: any): NormalizedAttr[] => {
  const raw = findRawAttrContainer(item);
  const list = normalizeAttrContainer(raw);
  if (list.length === 0) return list;

  const seenName = new Set<string>();
  const seenKey = new Set<string>();
  const dedup: NormalizedAttr[] = [];
  for (const a of list) {
    const k = `${a.attr_id ?? ''}__${(a.attr_name || '').toLowerCase()}`;
    if (seenKey.has(k)) continue;
    if (a.attr_name && seenName.has(a.attr_name.toLowerCase())) continue;
    seenKey.add(k);
    if (a.attr_name) seenName.add(a.attr_name.toLowerCase());
    dedup.push(a);
  }
  return dedup;
};

/* =========================================================
 * チームID解決 & payload正規化
 * ========================================================= */

// 「e-POWER チーム」みたいな末尾「チーム」や全角スペース差、大小を潰して比較
const normalizeTeamKey = (s?: string | null) =>
  String(s ?? '')
    .replace(/チーム$/i, '')
    .replace(/[\s\u3000]+/g, ' ')
    .trim()
    .toUpperCase();

/** deptTeamLinks を基準に、ラベル/値/数値っぽい文字列から team_id を解決 */
export const resolveTeamIdByHint = (hint: any, links: TeamDepartmentLink[]): number | null => {
  if (hint == null) return null;

  const n = Number(hint);
  if (Number.isFinite(n)) return n;

  const key = normalizeTeamKey(
    typeof hint === 'object' ? (hint as any).label ?? (hint as any).value ?? (hint as any).name ?? '' : String(hint)
  );
  if (!key) return null;

  const hit = (links ?? []).find((l) => {
    const a = normalizeTeamKey(l.team_label || '');
    const b = normalizeTeamKey(l.team_value || '');
    return a === key || b === key;
  });
  return typeof hit?.team_id === 'number' ? hit.team_id : null;
};

/** OverwriteInputSearchArea からの payload を「固定：チーム」「entries: チーム」の双方で数値IDに正規化 */
export const normalizeTeamInPayload = (p: any, links: TeamDepartmentLink[]): any => {
  if (!p) return p;

  const q: any = {
    ...p,
    fixed: p.fixed ? { ...p.fixed } : {},
    entries: Array.isArray(p.entries) ? [...p.entries] : p.entries,
  };

  if (q.fixed?.team) {
    const id = resolveTeamIdByHint(q.fixed.team, links);
    const label =
      typeof q.fixed.team === 'object'
        ? (q.fixed.team as any).label ?? (q.fixed.team as any).value ?? ''
        : String(q.fixed.team ?? '');
    if (id !== null) {
      q.fixed.team = { value: String(id), label: label || String(id) };
    }
  }

  if (Array.isArray(q.entries)) {
    q.entries = q.entries.map((e: any) => {
      const isTeam =
        String(e?.type ?? '').trim() === 'チーム' ||
        normalizeTeamKey(String(e?.type ?? '')) === 'TEAM' ||
        normalizeTeamKey(String(e?.label ?? '')) === 'TEAM';

      if (!isTeam) return e;

      const raw = e.raw;
      const toObj = (x: any) => {
        const id = resolveTeamIdByHint(x, links);
        const lbl = typeof x === 'object' ? x.label ?? x.value ?? x.name ?? '' : String(x ?? '');
        return id !== null ? { value: String(id), label: lbl || String(id) } : x;
      };

      if (Array.isArray(raw)) {
        return { ...e, raw: raw.map(toObj) };
      }
      return { ...e, raw: toObj(raw) };
    });
  }

  return q;
};

const isTeamPayloadEntry = (entry: any): boolean => {
  const type = String(entry?.type ?? '').trim();
  const label = String(entry?.label ?? '').trim();
  return (
    type === '繝√・繝' ||
    normalizeTeamKey(type) === 'TEAM' ||
    normalizeTeamKey(label) === 'TEAM'
  );
};

const pickTeamValueText = (value: any): string => {
  if (value == null) return '';
  if (typeof value === 'object') {
    return String((value as any).value ?? (value as any).id ?? (value as any).label ?? (value as any).name ?? '').trim();
  }
  return String(value).trim();
};

const pickTeamLabelText = (value: any): string => {
  if (value == null) return '';
  if (typeof value === 'object') {
    return String((value as any).label ?? (value as any).name ?? (value as any).value ?? (value as any).id ?? '').trim();
  }
  return String(value).trim();
};

/** Overwrite team changes must replace the previous payload team instead of re-normalizing stale data. */
export const applyTeamSelectionToPayload = (
  payload: any,
  teamValue: any,
  links: TeamDepartmentLink[]
): any => {
  const current = payload ?? { fixed: {}, entries: [] };
  const next: any = {
    ...current,
    fixed: current.fixed ? { ...current.fixed } : {},
    entries: Array.isArray(current.entries)
      ? current.entries.filter((entry: any) => !isTeamPayloadEntry(entry))
      : current.entries,
    meta: { ...(current.meta ?? {}) },
    extras: { ...(current.extras ?? {}) },
  };

  const rawValue = pickTeamValueText(teamValue);
  const rawLabel = pickTeamLabelText(teamValue);

  if (!rawValue && !rawLabel) {
    next.fixed.team = null;
    next.meta.teamCleared = true;
    next.extras.teamCleared = true;
    next.extras.teamClearedByUser = true;
    return next;
  }

  const resolvedId =
    resolveTeamIdByHint(rawValue, links) ??
    resolveTeamIdByHint(rawLabel, links) ??
    null;
  const hit =
    resolvedId != null
      ? (links ?? []).find((link) => Number(link.team_id) === Number(resolvedId))
      : null;
  const resolvedValue = resolvedId != null ? String(resolvedId) : rawValue || rawLabel;
  const explicitLabel = rawLabel && rawLabel !== rawValue ? rawLabel : '';
  const resolvedLabel =
    explicitLabel ||
    String(hit?.team_label ?? hit?.team_value ?? '').trim() ||
    rawLabel ||
    resolvedValue;

  next.fixed.team = { value: resolvedValue, label: resolvedLabel };
  delete next.meta.teamCleared;
  delete next.extras.teamCleared;
  delete next.extras.teamClearedByUser;

  return normalizeTeamInPayload(next, links);
};
